import React, { useState, useRef, useMemo } from 'react';
import ActionButton from '../../components/ActionButton';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import logo from "../../assets/img/Fronic_Logo_white.png";
import { useNavigate } from "react-router-dom";
import { saveObject, getObject, removeObject, toastError, toastSuccess } from '../../utils';
import LoginFormValidate from './LoginFormValidate';
import { adminLogin } from "./actions";
import FullScreenLoader from '../Common/FullScreenLoader';

import bgVideo1 from '../../assets/videos/dj-mixing.mp4';
import bgVideo3 from '../../assets/videos/dj-performance.mp4';
import bgVideo6 from '../../assets/videos/guitar-musician.mp4';
import bgVideo7 from '../../assets/videos/singer-spotlight.mp4';
import bgVideo8 from '../../assets/videos/equalizer-music.mp4';
import bgVideo9 from '../../assets/videos/neon.mp4';

const bgVideos = [bgVideo1, bgVideo3, bgVideo6, bgVideo7, bgVideo8, bgVideo9];

export default function Login() {
    const navigate = useNavigate();
    const [rememberMe, setRememberMe] = useState(() => {
        const saved = getObject('fronic_remember_me');
        return saved?.rememberMe || false;
    });
    const [user, setUser] = useState(() => {
        const saved = getObject('fronic_remember_me');
        if (saved?.rememberMe) {
            return { email: saved.email || '', password: saved.password || '' };
        }
        return { email: '', password: '' };
    });
    const [errors, setErrors] = useState({});
    const [loginLoader, setLoginLoader] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [isFocused, setIsFocused] = useState({ email: false, password: false });

    const videoRef = useRef(null);
    const selectedVideo = useMemo(() => bgVideos[Math.floor(Math.random() * bgVideos.length)], []);

    const onChangeHandler = ({ target: { value = '', name = '' } }) => {
        setUser({ ...user, [name]: value });
        setErrors({ ...errors, [name]: '' });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (loginLoader) return;
        const { errors, isValid } = LoginFormValidate(user);
        setErrors(errors);
        if (isValid) {
            setLoginLoader(true);
            adminLogin(user).then((res) => {
                saveObject('auth', res.token);
                if (rememberMe) {
                    saveObject('fronic_remember_me', { email: user.email, password: user.password, rememberMe: true });
                } else {
                    removeObject('fronic_remember_me');
                }
                toastSuccess(res.message || 'Login successful');
                setLoginLoader(false);
                return navigate('/');
            }).catch((err) => {
                setLoginLoader(false);
                toastError(err?.message || 'Something went wrong');
            });
        }
    };

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(15, 17, 23, 0.5)',
            backdropFilter: 'blur(12px)',
            borderRadius: '2px',
            height: 50,
            transition: 'all 0.3s ease',
            '& fieldset': {
                borderColor: 'rgba(255,255,255,0.08)',
                transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(108, 99, 255, 0.4)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#6C63FF',
                boxShadow: '0 0 0 3px rgba(108, 99, 255, 0.12)',
            },
        },
    };

    return (
        <>
            <FullScreenLoader open={loginLoader} />
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                {/* Background Video - full screen */}
                <video
                    ref={videoRef}
                    src={selectedVideo}
                    muted
                    autoPlay
                    loop
                    playsInline
                    onCanPlay={() => setVideoReady(true)}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0,
                        opacity: videoReady ? 1 : 0,
                        transition: 'opacity 1.5s ease',
                    }}
                />

                {/* Dark overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        background: 'linear-gradient(to right, rgba(15,17,23,0.95) 0%, rgba(15,17,23,0.85) 40%, rgba(15,17,23,0.4) 100%)',
                    }}
                />

                {/* Decorative vertical line accent */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: { xs: 24, sm: 48, md: '8%' },
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        background: 'linear-gradient(to bottom, transparent 10%, rgba(108,99,255,0.3) 30%, rgba(108,99,255,0.15) 70%, transparent 90%)',
                        zIndex: 2,
                    }}
                />

                {/* Floating decorative dots */}
                {[
                    { top: '15%', left: '5%', size: 6, delay: '0s', opacity: 0.4 },
                    { top: '25%', left: '12%', size: 4, delay: '1s', opacity: 0.3 },
                    { top: '70%', left: '8%', size: 5, delay: '2s', opacity: 0.25 },
                    { top: '80%', left: '3%', size: 3, delay: '3s', opacity: 0.35 },
                    { top: '45%', left: '15%', size: 4, delay: '1.5s', opacity: 0.2 },
                ].map((dot, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: dot.top,
                            left: dot.left,
                            width: dot.size,
                            height: dot.size,
                            borderRadius: '50%',
                            backgroundColor: '#6C63FF',
                            opacity: dot.opacity,
                            zIndex: 2,
                            animation: `float 4s ease-in-out ${dot.delay} infinite`,
                            '@keyframes float': {
                                '0%, 100%': { transform: 'translateY(0px)' },
                                '50%': { transform: 'translateY(-12px)' },
                            },
                        }}
                    />
                ))}

                {/* Ambient glow behind form */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '30%',
                        left: { xs: '20%', md: '15%' },
                        width: 400,
                        height: 400,
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(108,99,255,0.08) 0%, transparent 70%)',
                        zIndex: 1,
                        filter: 'blur(80px)',
                        animation: 'glow 8s ease-in-out infinite',
                        '@keyframes glow': {
                            '0%, 100%': { opacity: 0.5, transform: 'scale(1)' },
                            '50%': { opacity: 0.9, transform: 'scale(1.1)' },
                        },
                    }}
                />

                {/* Left side - Login Form */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 3,
                        width: { xs: '100%', md: '45%', lg: '40%' },
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        px: { xs: 3, sm: 6, md: 8, lg: 10 },
                        py: 4,
                    }}
                >
                    {/* Logo at top */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 36,
                            left: { xs: 24, sm: 48, md: 64, lg: 80 },
                        }}
                    >
                        <img src={logo} alt="Fronic" style={{ height: 40, opacity: 0.9 }} />
                    </Box>

                    {/* Form container with subtle background */}
                    <Box
                        sx={{
                            maxWidth: 400,
                            width: '100%',
                            background: 'rgba(26, 29, 39, 0.45)',
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            borderRadius: '12px',
                            p: { xs: 3, sm: 4 },
                        }}
                    >
                        {/* Decorative line above form */}
                        <Box
                            sx={{
                                width: 40,
                                height: 3,
                                borderRadius: 2,
                                background: 'linear-gradient(90deg, #6C63FF, #8B5CF6)',
                                mb: 4,
                            }}
                        />

                        <form onSubmit={onSubmit} noValidate>
                            <TextField
                                type="text"
                                variant="outlined"
                                fullWidth
                                id="email"
                                placeholder="Email"
                                name="email"
                                error={!!errors?.email}
                                helperText={errors?.email || ''}
                                value={user.email}
                                onChange={onChangeHandler}
                                onFocus={() => setIsFocused(p => ({ ...p, email: true }))}
                                onBlur={() => setIsFocused(p => ({ ...p, email: false }))}
                                autoComplete="email"
                                autoFocus
                                sx={{ ...inputSx, mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Mail size={18} color={isFocused.email ? '#6C63FF' : '#64748B'} style={{ transition: 'color 0.3s' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                variant="outlined"
                                fullWidth
                                name="password"
                                placeholder="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                error={!!errors?.password}
                                helperText={errors?.password || ''}
                                value={user.password}
                                onChange={onChangeHandler}
                                onFocus={() => setIsFocused(p => ({ ...p, password: true }))}
                                onBlur={() => setIsFocused(p => ({ ...p, password: false }))}
                                autoComplete="current-password"
                                sx={{ ...inputSx, mb: 1.5 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock size={18} color={isFocused.password ? '#6C63FF' : '#64748B'} style={{ transition: 'color 0.3s' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                size="small"
                                                sx={{
                                                    color: '#64748B',
                                                    '&:hover': { color: '#6C63FF' },
                                                    transition: 'color 0.2s',
                                                }}
                                            >
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Remember Me */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        sx={{
                                            color: '#64748B',
                                            '&.Mui-checked': { color: '#6C63FF' },
                                        }}
                                    />
                                }
                                label="Remember me"
                                sx={{
                                    mb: 2,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.8rem',
                                        color: '#94A3B8',
                                    },
                                }}
                            />

                            <ActionButton
                                type="submit"
                                variant="settle"
                                sx={{
                                    width: '100%',
                                    height: 50,
                                    fontSize: '0.95rem',
                                    gap: 1,
                                }}
                            >
                                Login <ArrowRight size={18} />
                            </ActionButton>
                        </form>

                        {/* Decorative bottom divider */}
                        <Box
                            sx={{
                                mt: 4,
                                height: '1px',
                                background: 'linear-gradient(to right, #6C63FF, #8B5CF6, transparent)',
                                opacity: 0.3,
                                borderRadius: 1,
                            }}
                        />
                    </Box>
                </Box>

                {/* Right side - visible video area (only on larger screens) */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'relative',
                        zIndex: 2,
                        flex: 1,
                    }}
                />
            </Box>
        </>
    );
}
